/**
 * Bada shipping UI: amounts from the `shipping_market` metaobject in the shopper's currency.
 *
 * Liquid (snippets/bada-shipping-*.liquid) renders every amount it can format exactly. Amounts stored in another
 * currency arrive as `<bdi class="bada-shipping-money" data-bada-amount data-bada-currency>` with the stored currency. This
 * module converts them with `Shopify.currency.rate` (shop currency to presentment currency) and rounds UP to a whole
 * unit, so a shopper who reaches the shown threshold always qualifies at checkout. An amount it cannot convert keeps
 * its stored currency: no number is ever invented.
 *
 * Elements:
 * - <bada-shipping-line>: the announcement line; converts its amounts.
 * - <bada-shipping-progress>: the bag progress; recomputes the state when its data attributes change (cart re-render).
 * - <bada-shipping-bridge>: "Close the gap" picks; shown only when a real gap exists and a pick can help.
 */
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {object} MoneyContext
 * @property {string} active - Presentment currency ISO code
 * @property {string} shop - Shop currency ISO code
 * @property {number} rate - Shop to presentment conversion rate (NaN when unknown)
 * @property {string} format - Money format for the presentment currency
 */

const ROUNDING_EPSILON = 1e-9;

/**
 * Number of decimals the store's money format uses for a currency (same table as the `money` filter replica).
 * @param {string} currency
 */
function precisionOf(currency) {
  const decimals = formatMoney(1, '{{amount}}', currency).split('.')[1];
  return decimals ? decimals.length : 0;
}

/** @param {string} format */
function wholeFormat(format) {
  return format
    .replace(/{{\s*amount\s*}}/g, '{{amount_no_decimals}}')
    .replace(/{{\s*amount_with_comma_separator\s*}}/g, '{{amount_no_decimals_with_comma_separator}}')
    .replace(/{{\s*amount_with_space_separator\s*}}/g, '{{amount_no_decimals_with_space_separator}}');
}

/** @param {string} value */
function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @param {HTMLElement} host - Element carrying the money attributes (snippets/bada-shipping-money-attributes.liquid)
 * @returns {MoneyContext}
 */
function moneyContext(host) {
  const { activeCurrency = '', shopCurrency = '', moneyFormat = '{{amount}}', moneyFormatCurrency = '' } = host.dataset;
  const live = window.Shopify?.currency;
  let rate = Number.NaN;

  if (activeCurrency === shopCurrency) {
    rate = 1;
  } else if (live && live.active === activeCurrency) {
    rate = Number(live.rate);
  }

  return {
    active: activeCurrency,
    shop: shopCurrency,
    rate,
    format: activeCurrency !== shopCurrency && moneyFormatCurrency ? moneyFormatCurrency : moneyFormat,
  };
}

/**
 * Converts a stored amount into the presentment currency.
 * @param {number} amount - Amount in major units
 * @param {string} currency - Currency the amount is stored in
 * @param {MoneyContext} context
 * @returns {number | null} Major units in the presentment currency, or null when it cannot be converted
 */
function toPresentment(amount, currency, context) {
  if (!Number.isFinite(amount)) return null;
  if (currency === context.active) return amount;
  if (currency !== context.shop || !Number.isFinite(context.rate) || context.rate <= 0) return null;

  return Math.ceil(amount * context.rate - ROUNDING_EPSILON);
}

/**
 * @param {number} value - Major units in the presentment currency
 * @param {MoneyContext} context
 */
function formatAmount(value, context) {
  const precision = precisionOf(context.active);
  const factor = 10 ** precision;
  const minor = Math.round(value * factor);
  const isWhole = minor % factor === 0;

  return formatMoney(minor, isWhole ? wholeFormat(context.format) : context.format, context.active);
}

/**
 * Rounds major units to the currency's precision.
 * @param {number} value
 * @param {MoneyContext} context
 */
function roundToCurrency(value, context) {
  const factor = 10 ** precisionOf(context.active);
  return Math.round(value * factor) / factor;
}

/**
 * Converts every `.bada-shipping-money` amount inside a host that is not already in the presentment currency.
 * @param {HTMLElement} host
 */
function convertAmounts(host) {
  const context = moneyContext(host);

  for (const element of host.querySelectorAll('.bada-shipping-money')) {
    if (!(element instanceof HTMLElement)) continue;
    const currency = element.dataset.badaCurrency ?? '';
    if (currency === context.active || element.dataset.badaConverted === 'true') continue;

    const value = toPresentment(Number(element.dataset.badaAmount), currency, context);
    if (value === null) continue;

    element.textContent = formatAmount(value, context);
    element.dataset.badaConverted = 'true';
  }
}

/**
 * A host for shipping amounts (announcement line, PDP shipping row, home rates table, newsletter offer).
 * Converts its amounts on connect and again when a re-render (section morph) puts server text back.
 */
class BadaShippingLine extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    convertAmounts(this);

    this.#observer = new MutationObserver(() => {
      if (this.#frame !== undefined) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = undefined;
        // Converted amounts carry data-bada-converted, so this settles after one pass.
        convertAmounts(this);
      });
    });
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-bada-amount', 'data-bada-converted'],
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }
}

/**
 * @typedef {object} ShippingState
 * @property {string} state
 * @property {number | null} left - Amount left to free shipping, presentment major units
 * @property {MoneyContext} context
 */

/** Bag progress toward free shipping. */
class BadaShippingProgress extends HTMLElement {
  static observedAttributes = [
    'data-mode',
    'data-threshold',
    'data-currency',
    'data-eligible-cents',
    'data-item-count',
    'data-bulky-count',
  ];

  /** @type {ShippingState | null} */
  shippingState = null;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    this.#render();
  }

  disconnectedCallback() {
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  attributeChangedCallback() {
    if (!this.isConnected || this.#frame !== undefined) return;

    // A cart re-render updates several attributes in a row: render once, after the morph.
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#render();
    });
  }

  /**
   * @param {string} key - Template data attribute name (camel case, without the `t` prefix)
   * @param {Record<string, string>} [values]
   */
  #template(key, values = {}) {
    let text = this.dataset[`t${key}`] ?? '';
    for (const [name, value] of Object.entries(values)) {
      text = text.split(`[${name}]`).join(value);
    }
    return text;
  }

  #render() {
    const context = moneyContext(this);
    const text = this.querySelector('[data-bada-ship-text]');
    const bar = this.querySelector('[data-bada-ship-bar]');
    const { mode, currency = '' } = this.dataset;

    if (mode !== 'threshold' || !(text instanceof HTMLElement) || !(bar instanceof HTMLElement)) {
      convertAmounts(this);
      this.#publish({ state: mode ?? 'none', left: null, context });
      return;
    }

    const threshold = toPresentment(Number(this.dataset.threshold), currency, context);
    if (threshold === null) {
      // No rate for this currency: keep the threshold line Liquid rendered in the stored currency.
      convertAmounts(this);
      bar.hidden = true;
      this.#publish({ state: 'convert', left: null, context });
      return;
    }

    const eligible = Number(this.dataset.eligibleCents) / 100;
    const itemCount = Number(this.dataset.itemCount);
    const bulkyCount = Number(this.dataset.bulkyCount);
    const left = Math.max(0, roundToCurrency(threshold - eligible, context));

    let state;
    let html;
    let percent = 0;
    let valueText = '';

    if (bulkyCount > 0 && bulkyCount >= itemCount) {
      state = 'bulky-only';
      html = this.#template('BulkyOnly');
    } else if (left <= 0) {
      state = bulkyCount > 0 ? 'reached-bulky' : 'reached';
      html = this.#template(bulkyCount > 0 ? 'ReachedBulky' : 'Reached');
      percent = 100;
      valueText = this.#template('ValueReached');
    } else {
      percent = Math.min(100, Math.max(0, (eligible / threshold) * 100));
      const amount = `<bdi>${escapeHtml(formatAmount(left, context))}</bdi>`;
      state = percent >= 70 ? 'near' : 'below';
      html = this.#template(state === 'near' ? 'Near' : 'Below', { amount });
      valueText = this.#template('Value', {
        subtotal: formatAmount(eligible, context),
        threshold: formatAmount(threshold, context),
      });
    }

    if (text.innerHTML !== html) text.innerHTML = html;
    this.dataset.state = state;

    const showBar = state !== 'bulky-only';
    bar.hidden = !showBar;
    if (showBar) {
      bar.setAttribute('aria-valuenow', String(Math.round(percent)));
      if (valueText) bar.setAttribute('aria-valuetext', valueText.replace(/<[^>]*>/g, ''));
      bar.style.setProperty('--bada-ship-fill', (percent / 100).toFixed(4));
    }

    this.#publish({ state, left: state === 'below' || state === 'near' ? left : 0, context });
  }

  /** @param {ShippingState} state */
  #publish(state) {
    this.shippingState = state;
    this.dispatchEvent(new CustomEvent('bada:shipping-state', { bubbles: true, detail: state }));
  }
}

/** "Close the gap" picks under the bag lines. */
class BadaShippingBridge extends HTMLElement {
  static observedAttributes = ['data-max-gap', 'data-signature'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('bada:shipping-state', this.#schedule, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  #schedule = () => {
    if (this.#frame !== undefined) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  /** @returns {BadaShippingProgress | null} */
  #progress() {
    const scope = this.closest('cart-items-component') ?? document;
    const progress = scope.querySelector('bada-shipping-progress') ?? document.querySelector('bada-shipping-progress');
    return progress instanceof BadaShippingProgress ? progress : null;
  }

  #update() {
    const state = this.#progress()?.shippingState;
    const context = moneyContext(this);
    const maxGap = toPresentment(Number(this.dataset.maxGap), this.dataset.maxGapCurrency ?? '', context);
    const left = state?.left ?? null;

    if (left === null || !(left > 0) || maxGap === null || left > maxGap) {
      this.hidden = true;
      return;
    }

    const items = [...this.querySelectorAll('[data-bada-bridge-item]')].filter(
      (item) => item instanceof HTMLElement
    );
    const priced = items.map((item) => ({
      item: /** @type {HTMLElement} */ (item),
      price: Number(/** @type {HTMLElement} */ (item).dataset.priceCents) / 100,
    }));

    // The cheapest picks that close the gap on their own, then the closest ones below it.
    const reach = priced.filter((entry) => entry.price >= left).sort((a, b) => a.price - b.price);
    const near = priced.filter((entry) => entry.price < left).sort((a, b) => b.price - a.price);
    const picks = [...reach, ...near].slice(0, 3);

    if (!picks.length) {
      this.hidden = true;
      return;
    }

    for (const entry of priced) entry.item.hidden = true;
    picks.forEach((entry, index) => {
      entry.item.hidden = false;
      entry.item.style.order = String(index);
    });

    const title = this.querySelector('[data-bada-bridge-title]');
    const closes = (picks[0]?.price ?? 0) >= left;
    if (title) title.textContent = (closes ? this.dataset.tClose : this.dataset.tCloser) ?? '';

    this.hidden = false;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-bada-bridge-add]') : null;
    if (!(target instanceof HTMLButtonElement) || target.getAttribute('aria-busy') === 'true') return;

    this.#add(target);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const routes = typeof Theme !== 'undefined' ? Theme.routes : null;
    const addUrl = routes?.cart_add_url ?? `${window.Shopify?.routes?.root ?? '/'}cart/add.js`;
    const cartUrl = `${routes?.cart_url ?? `${window.Shopify?.routes?.root ?? '/'}cart`}.js`;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((res) => res.json());

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to bag failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'bada-shipping-bridge', itemCount: 1 },
        });
        button.removeAttribute('aria-busy');
        this.#announce(String(data.description || data.message || ''));
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'bada-shipping-bridge',
          itemCount: 1,
          sections: data.sections,
        },
      });
      this.#announce(this.dataset.tAdded ?? '');
      // The added product leaves the picks once the bag re-renders (the progress publishes its new state after the
      // morph): keep focus inside the bag instead of dropping it to the page (WCAG 2.4.3).
      document.addEventListener('bada:shipping-state', () => requestAnimationFrame(() => this.#restoreFocus()), {
        once: true,
      });
    } catch (error) {
      deferred.reject(error);
      button.removeAttribute('aria-busy');
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to bag',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-bada-bridge-status]');
    if (!status || !message) return;
    status.textContent = '';
    setTimeout(() => {
      status.textContent = message;
    }, 40);
  }

  #restoreFocus() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.isConnected && active !== document.body && active.offsetParent) return;

    const drawer = this.closest('dialog') ?? document;
    const next = this.isConnected && !this.hidden ? this.querySelector('[data-bada-bridge-item]:not([hidden]) button') : null;
    const heading = drawer.querySelector('#cart-drawer-heading');
    const target = next ?? heading;

    if (target instanceof HTMLElement) {
      if (target === heading && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus();
    }
  }
}

if (!customElements.get('bada-shipping-line')) customElements.define('bada-shipping-line', BadaShippingLine);
if (!customElements.get('bada-shipping-progress')) customElements.define('bada-shipping-progress', BadaShippingProgress);
if (!customElements.get('bada-shipping-bridge')) customElements.define('bada-shipping-bridge', BadaShippingBridge);
